import type {Editor} from 'grapesjs';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {redirectUser} from '@Utils/browser.util';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {getEmailMakerStoreOpts} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/storage';
import {
  canCurrentUserAutosaveTemplate,
  getCurrentIdEmailCampaign,
  hasEmailTemplateSavedOnce,
  saveEmailTemplateToCache,
  setEmailMakerAutosaveEnabled,
} from '@EmailMakerReact/lib/email-maker-storage-library';
import {reloadPremiumBackgrounds} from '@EmailMakerReact/lib/email-maker-design-library';

const AUTHENTICATE_SIGNUP_SOURCE = 'email-editor';

const UPDATE_CAMPAIGN_SCHEDULE_ENDPOINT = 'emailmarketing/updateEmailMarketingCampaignSchedule';
const PUBLISH_CAMPAIGN_PATH = 'emailmarketing/publish/campaign';

/** PMW cache key the pricing/checkout flow prefills the schedule date under. */
const SCHEDULING_CACHE_KEY = 'emc_schedule';

export interface AuthenticateUserOptions {
  /** Runs once the user is authenticated (immediately when already logged in). */
  cb?: VoidFunction;
  /** Runs when the auth modal is dismissed without logging in. */
  onEarlyClose?: VoidFunction;
}

/** Runs the callback if logged in, otherwise opens the auth modal and runs it after login. */
export function authenticateUser(editor: Editor, options: AuthenticateUserOptions = {}): void {
  if (isUserLoggedIn()) {
    options.cb?.();
    return;
  }

  openAuthenticateModal({
    fbPermissions: '',
    signupSource: AUTHENTICATE_SIGNUP_SOURCE,
    panelText: window.i18next.t('pmwjs_make_account_to_save_email'),
    panelSubtext: window.i18next.t('pmwjs_login_emails_promotion_subtext'),
    onLoginSuccess: (): Promise<void> => {
      void onUserLoggedIn(editor);
      options.cb?.();
      return Promise.resolve();
    },
    onCloseCb: (): void => {
      options.onEarlyClose?.();
    },
  });
}

/** For template owners, re-enables autosave, saves immediately, and re-resolves premium background URLs. */
export async function onUserLoggedIn(editor: Editor): Promise<void> {
  if (!canCurrentUserAutosaveTemplate(editor)) {
    return;
  }

  setEmailMakerAutosaveEnabled(editor, true);
  await saveEmailTemplateToCache(editor);
  reloadPremiumBackgrounds(editor);
}

/** Auth + premium gates, full save, schedule update for scheduled campaigns, then the publish redirect. */
export async function saveAndPublish(editor: Editor): Promise<void> {
  if (!isUserLoggedIn()) {
    authenticateUser(editor, {
      cb: (): void => {
        void saveAndPublish(editor);
      },
    });
    return;
  }

  if (!isUserPremium()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN);
    return;
  }

  const saved = await saveEmailTemplateToCache(editor);
  if (saved === null) {
    return;
  }

  const {isScheduled, scheduledTime} = getEmailMakerStoreOpts(editor);
  if (isScheduled && hasEmailTemplateSavedOnce(editor)) {
    await window.PMW.write(window.PMW.util.site_url(UPDATE_CAMPAIGN_SCHEDULE_ENDPOINT), {
      isScheduled,
      idEmailMarketingCampaign: getCurrentIdEmailCampaign(editor),
      scheduledDateTime: scheduledTime ?? null,
    });
  }

  validateTemplateAndPublish(editor);
}

/** Redirects to the campaign publish page. */
export function validateTemplateAndPublish(editor: Editor): void {
  const id = getCurrentIdEmailCampaign(editor);
  if (!id) {
    return;
  }

  redirectUser(window.PMW.util.site_url(`${PUBLISH_CAMPAIGN_PATH}/${id}`), false, false);
}

/** Returns the schedule date the checkout flow cached, when present and parseable. */
export function getPrefilledScheduleDate(): Date | null {
  const prefilledDateTimestamp = window.PMW.getCacheItem(SCHEDULING_CACHE_KEY) as string | null;

  if (prefilledDateTimestamp) {
    const schedulingDate = Date.parse(prefilledDateTimestamp);

    if (schedulingDate) {
      return new Date(schedulingDate);
    }
  }

  return null;
}

/** Clears the cached schedule date. */
export function deletePrefilledScheduleDate(): void {
  window.PMW.deleteCacheItem(SCHEDULING_CACHE_KEY);
}
